<template>
  <div class="overflow-x-auto bg-white rounded-lg shadow-sm border border-gray-100">
    <table class="w-full text-sm table-fixed">
      <colgroup>
        <col class="w-[70%]" />
        <col class="w-[10%]" />
        <col class="w-[10%]" />
        <col class="w-[10%]" />
      </colgroup>
      <thead class="sticky top-0 bg-gray-50 border-b text-xs uppercase text-gray-500">
        <tr>
          <th class="text-left py-3 px-4">
            <button
              class="inline-flex items-center gap-1 hover:text-gray-700"
              @click="emit('sort', 'name')"
              type="button"
            >
              Company
              <ArrowUpDown v-if="!isActiveSort('name')" class="w-3 h-3 text-gray-400" />
              <ArrowUp v-else-if="isAscending('name')" class="w-3 h-3 text-gray-700" />
              <ArrowDown v-else class="w-3 h-3 text-gray-700" />
            </button>
          </th>
          <th class="text-center py-3 px-4">
            <button
              class="inline-flex items-center gap-1 hover:text-gray-700"
              @click="emit('sort', 'open_role_count')"
              type="button"
            >
              Jobs
              <ArrowUpDown v-if="!isActiveSort('open_role_count')" class="w-3 h-3 text-gray-400" />
              <ArrowUp v-else-if="isAscending('open_role_count')" class="w-3 h-3 text-gray-700" />
              <ArrowDown v-else class="w-3 h-3 text-gray-700" />
            </button>
          </th>
          <th class="text-center py-3 px-4">
            <button
              class="inline-flex items-center gap-1 hover:text-gray-700"
              @click="emit('sort', 'interview_count')"
              type="button"
            >
              Interviews
              <ArrowUpDown v-if="!isActiveSort('interview_count')" class="w-3 h-3 text-gray-400" />
              <ArrowUp v-else-if="isAscending('interview_count')" class="w-3 h-3 text-gray-700" />
              <ArrowDown v-else class="w-3 h-3 text-gray-700" />
            </button>
          </th>
          <th class="text-center py-3 px-4">
            <button
              class="inline-flex items-center gap-1 hover:text-gray-700"
              @click="emit('sort', 'offer_count')"
              type="button"
            >
              Offers
              <ArrowUpDown v-if="!isActiveSort('offer_count')" class="w-3 h-3 text-gray-400" />
              <ArrowUp v-else-if="isAscending('offer_count')" class="w-3 h-3 text-gray-700" />
              <ArrowDown v-else class="w-3 h-3 text-gray-700" />
            </button>
          </th>
        </tr>
      </thead>
      <tbody :class="{ 'opacity-50 pointer-events-none': loading }">
        <tr
          v-for="company in companies"
          :key="company.id"
          class="border-b last:border-b-0 hover:bg-gray-50 transition-colors duration-150"
        >
          <td class="py-3 px-4">
            <div class="flex items-center gap-3">
              <img
                v-if="company.logo"
                :src="company.logo"
                :alt="`${company.name} logo`"
                class="w-8 h-8 rounded object-contain bg-gray-50"
              />
              <div
                v-else
                class="w-8 h-8 rounded bg-gray-100 flex items-center justify-center"
              >
                <Building class="w-4 h-4 text-gray-400" />
              </div>
              <div class="flex flex-col">
                <div class="flex items-center gap-2">
                  <a
                    v-if="company.url"
                    :href="company.url"
                    class="font-medium text-gray-900 hover:text-cosmicPurple underline decoration-dotted underline-offset-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ company.name }}
                  </a>
                  <span v-else class="font-medium text-gray-900">{{ company.name }}</span>
                  <button
                    v-if="showEdit"
                    class="text-gray-400 hover:text-cosmicPurple"
                    type="button"
                    aria-label="Edit company"
                    @click="emit('edit', company)"
                  >
                    <Pencil class="w-3.5 h-3.5" />
                  </button>
                </div>
                <!-- Notes are internal programme information — hidden for limited users -->
                <span
                  v-if="company.job_application_notes && !isLimitedUser"
                  class="text-sm text-gray-500 mt-1 line-clamp-2"
                >
                  {{ company.job_application_notes }}
                </span>
              </div>
            </div>
          </td>
          <td class="text-center py-3 px-4">
            <a
              :href="getTrackTalentUrl(company.id)"
              class="text-cosmicPurple hover:text-cosmicPurple/80 underline decoration-dotted underline-offset-2"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ company.open_role_count }}
            </a>
          </td>
          <td class="text-center py-3 px-4">
            <a
              :href="getInterviewWatchUrl(company.id)"
              class="text-cosmicPurple hover:text-cosmicPurple/80 underline decoration-dotted underline-offset-2"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ company.interview_count }}
            </a>
          </td>
          <td class="text-center py-3 px-4">
            <a
              :href="getOffersUrl(company.id)"
              class="text-cosmicPurple hover:text-cosmicPurple/80 underline decoration-dotted underline-offset-2"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ company.offer_count }}
            </a>
          </td>
        </tr>
        <tr v-if="!companies.length && !loading">
          <td class="py-6 px-4 text-center text-gray-500" colspan="4">No companies found.</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { Building, ArrowUp, ArrowDown, ArrowUpDown, Pencil } from 'lucide-vue-next'
import type { ReferringCompany } from '@/types/company'
import { useAuth } from '@/composables/useAuth'

const { isLimitedUser } = useAuth()

const props = defineProps<{
  companies: ReferringCompany[]
  loading?: boolean
  ordering?: string
  showEdit?: boolean
}>()

const emit = defineEmits<{
  (event: 'sort', field: string): void
  (event: 'edit', company: ReferringCompany): void
}>()

const getTrackTalentUrl = (companyId: number) =>
  `https://tracktalent.outtalent.com/jobs?status=true&employer_ids=${encodeURIComponent(
    companyId.toString()
  )}&page=1`

const getInterviewWatchUrl = (companyId: number) =>
  `https://interviewwatch.outtalent.com/?employers=${encodeURIComponent(companyId.toString())}`

const getOffersUrl = (companyId: number) =>
  `https://offers.outtalent.com/?employers=${encodeURIComponent(companyId.toString())}`

const isAscending = (field: string) => props.ordering === field
const isDescending = (field: string) => props.ordering === `-${field}`
const isActiveSort = (field: string) => isAscending(field) || isDescending(field)
</script>
